// Copyright (c) Ping. All rights reserved.
//
// In-call "Call Thread" panel — now the SAME shared `<ThreadOpenPanel>` the main
// chat thread uses, scoped to the call's thread root (the call_invite message's
// content hash). Replies are real threaded `com.ping.text` messages, so they
// persist + sync + appear in the chat-room thread the call tile's "Call threads"
// button opens — one unified thread. The host (`ActiveCallView`) builds the
// conversation-scoped gateway and passes it in so the same instance also feeds
// the unread badge while the panel is closed.

import { useEffect, useMemo } from 'react';
import { Text,View } from 'react-native';

import {
  type StagedAttachment,
  type ThreadComposerProps,
  type ThreadGateway,
  type ThreadIdentifier,
  ThreadOpenPanel,
  type ThreadRowActions,
  useThreadViewModel,
} from '@ping/ui';

import { useCallStore } from '@calls/store/callStore';

/**
 * In-call thread panel. The host (`ActiveCallView`) now forwards the SAME
 * `rowActions` (reactions, message actions, retry, read-receipt resolvers,
 * media variants) and rich `composerProps` (mentions / attachments / voice
 * / GIF) the main chat thread uses, so the in-call thread has feature
 * parity with the chat-room thread rather than being text-only.
 */
export interface CallThreadPanelProps {
  readonly gateway: ThreadGateway;
  readonly rowActions?: ThreadRowActions | undefined;
  readonly composerProps?: ThreadComposerProps | undefined;
  readonly stagedAttachments?: readonly StagedAttachment[] | undefined;
  readonly onRemoveStagedAttachment?: ((id: string) => void) | undefined;
  readonly onFlushStagedAttachments?: (() => Promise<void> | void) | undefined;
}

export function CallThreadPanel({
  gateway,
  rowActions,
  composerProps,
  stagedAttachments,
  onRemoveStagedAttachment,
  onFlushStagedAttachments,
}: CallThreadPanelProps): JSX.Element {
  const root = useCallStore((s) => s.callThreadRoot);
  const vm = useThreadViewModel({ gateway });
  const { open, close } = vm;

  // Open the call's thread when its root is known; close on unmount.
  useEffect(() => {
    if (root !== null) open(root as ThreadIdentifier);
    return () => close();
  }, [root, open, close]);

  // Route the panel's close affordance to the call panel's close.
  const wrappedVm = useMemo(
    () => ({
      ...vm,
      close: () => {
        close();
        useCallStore.getState().setPanel('none');
      },
    }),
    [vm, close],
  );

  return (
    <View className="h-full w-[360px] overflow-hidden rounded-[12px] border border-light-border-secondary bg-light-surface-panel dark:border-dark-surface-elevated dark:bg-dark-surface-panel">
      {root === null ? (
        <View className="flex-1 items-center justify-center p-4">
          <Text className="text-center text-[14px] leading-[20px] text-light-text-muted dark:text-dark-text-muted">
            This call has no thread yet. Send a message to start one.
          </Text>
        </View>
      ) : (
        <ThreadOpenPanel
          vm={wrappedVm}
          focused
          {...(rowActions !== undefined ? { rowActions } : {})}
          {...(composerProps !== undefined ? { composerProps } : {})}
          {...(stagedAttachments !== undefined ? { stagedAttachments } : {})}
          {...(onRemoveStagedAttachment !== undefined ? { onRemoveStagedAttachment } : {})}
          {...(onFlushStagedAttachments !== undefined ? { onFlushStagedAttachments } : {})}
        />
      )}
    </View>
  );
}
